import { useTiers } from "~/api/swr/useTiers";
import { DocumentVersionStatus } from "~/config/types";
import { useDocumentVersions } from "~/api/v2/useDocumentVersions";
import { type DocumentVersionSummary, type DocumentDetails } from "~/api/types";
import { type Option } from "~/molecules/ControlledCombobox";
import { tierIdFormValueToNumberOrNull } from "~/helpers/tier";

interface UseDocumentLinkingProps {
  document: DocumentDetails;
  selectedTierId: string;
  selectedParentDocuments: Option[];
  selectedChildDocuments: Option[];
}

function getDocumentOptions(
  documentVersions: DocumentVersionSummary[],
  tierId: number | undefined,
  existingDocs: Option[],
  currentDocId: number,
  currentCategoryId: number,
): Option[] {
  if (!tierId) return [];

  const byCategoryThenTitle = (
    a: DocumentVersionSummary,
    b: DocumentVersionSummary,
  ) => {
    const aSame = a.document.category.id === currentCategoryId;
    const bSame = b.document.category.id === currentCategoryId;
    if (aSame !== bSame) return aSame ? -1 : 1;
    if (a.document.category.id !== b.document.category.id) {
      return a.document.category.title.localeCompare(b.document.category.title);
    }
    return a.title.localeCompare(b.title);
  };

  return documentVersions
    .filter(
      (el) =>
        el.document.tier !== null &&
        el.document.tier.id === tierId &&
        !existingDocs.some((doc) => doc.value === `${el.document.id}`) &&
        el.document.id !== currentDocId,
    )
    .sort(byCategoryThenTitle)
    .map((el) => ({
      value: `${el.document.id}`,
      label: el.title,
      subtitle: el.document.category.title,
    }));
}

export function useDocumentLinking({
  document,
  selectedTierId,
  selectedParentDocuments,
  selectedChildDocuments,
}: UseDocumentLinkingProps) {
  const { data: tiers } = useTiers();

  const { data: documentVersions } = useDocumentVersions({
    statuses: [DocumentVersionStatus.published],
  });

  const isLoading = !document || !tiers || !documentVersions;

  if (isLoading) {
    return { parentContext: null, childContext: null, isLoading };
  }

  const tierId = tierIdFormValueToNumberOrNull(selectedTierId);

  if (tierId === null) {
    return { parentContext: null, childContext: null, isLoading: false };
  }

  const tierIndex = tiers.findIndex((t) => t.id === tierId);
  const parentTier = tiers[tierIndex - 1];
  const childTier = tiers[tierIndex + 1];

  const parentContext = parentTier
    ? {
        options: getDocumentOptions(
          documentVersions,
          parentTier.id,
          selectedParentDocuments,
          document.id,
          document.category.id,
        ),
        tierName: parentTier.title,
      }
    : null;

  const childContext = childTier
    ? {
        options: getDocumentOptions(
          documentVersions,
          childTier.id,
          selectedChildDocuments,
          document.id,
          document.category.id,
        ),
        tierName: childTier.title,
      }
    : null;

  return {
    parentContext,
    childContext,
    isLoading,
  };
}
